// services/userService.ts
import apiClient from "@/plugins/apiClient";
import store from "@/store";
import { AxiosError } from "axios";

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

/**
 * LINEアクセストークンが無効か確認
 */
function isTokenExpired(error: unknown) {
  if (
    error &&
    typeof error === "object" &&
    error !== null &&
    "response" in error
  ) {
    const err = error as AxiosError;
    return (
      (err.response && err.response.status === 401) ||
      (err.response &&
        typeof err.response.data === "object" &&
        (err.response.data as any).error === "invalid_token")
    );
  }
  return false;
}

export async function fetchUser(): Promise<ApiLogin> {
  return await apiClient
    .get(`/api/wp-json/31corp/v1/user`)
    .then((res) => {
      return res.data;
    })
    .catch((e: any) => {
      const status = e.response.status || 0;
      const message = e.response.data.message || e.message || "API Error";
      throw new ApiError(status, message);
    });
}

export async function fetchToken(params: TokenParams): Promise<ApiLogin> {
  return await apiClient
    .post(
      `/api/wp-json/jwt-auth/v1/token?lang=${store.getters["auth/lang"]}`,
      params
    )
    .then((res) => {
      return res.data;
    })
    .catch((e: any) => {
      const status = e.response.status || 0;
      const message = e.response.data.message || e.message || "API Error";
      throw new ApiError(status, message);
    });
}

/**
 * LINE ID トークンでログインする
 * @param params Line ID トークン
 * @returns 31corp トークン
 */
export async function fetchLineToken(line_id_token: string, line_access_token?: string): Promise<ApiLogin> {
  const headers = {
    "X-Line-Id-Token": `${line_id_token}`,
    ...(line_access_token ? { "X-Line-Access-Token": `${line_access_token}` } : {}),
  }
  // alert(`headers: ${JSON.stringify(headers)}`);
  return await apiClient
    .post(
      `/api/wp-json/jwt-auth/v1/token/line?lang=${store.getters["auth/lang"]}`,
      {},
      {
        headers
      }
    )
    .then((res) => {
      return res.data;
    })
    .catch(async (e: any) => {
      if (isTokenExpired(e)) {
        await store.dispatch("auth/lineRefresh");
        return await apiClient
          .post(
            `/api/wp-json/jwt-auth/v1/token/line?lang=${store.getters["auth/lang"]}`,
            {},
            {
              headers: {
                "X-Line-Id-Token": `${line_id_token}`,
              },
            }
          )
          .then((res) => {
            return res.data;
          });
      } else {
        const status = e.response.status || 0;
        const message = e.response.data.message || e.message || "API Error";
        const code = e.response.data.code || e.code || "API Error";
        if (status === 500 && code === "Cannot access LINE REST Server") {
          throw new ApiError(500, "Line connect Error");
        }
        throw new ApiError(status, message);
      }
    });
}

export async function fetchLogout(): Promise<ApiLogout> {
  return await apiClient
    .get("/api/wp-json/jwt-auth/v1/logout")
    .then((res) => {
      return res.data;
    })
    .catch((e: any) => {
      const status = e.response.status || 0;
      const message = e.response.data.message || e.message || "API Error";
      throw new ApiError(status, message);
    });
}

export async function fetchPosts(params: {
  cat: string;
  offset?: number;
  length?: number;
  id?: number;
  lang?: string;
}): Promise<ApiPost> {
  const res = await apiClient.get("/api/wp-json/31corp/v1/posts", { params });
  return res.data;
}

export async function fetchPostsCampaign(params: {
  offset?: number;
  length?: number;
  id?: number;
  lang?: string;
}): Promise<ApiPostsCampaign> {
  const mergedParams = Object.assign({}, params, { cat: "campaign" });
  const res = await apiClient.get("/api/wp-json/31corp/v1/posts", {
    params: mergedParams,
  });
  return res.data;
}

export async function fetchPostsPickup(params: {
  offset?: number;
  length?: number;
  id?: number;
  lang?: string;
}): Promise<ApiPostsPickeup> {
  const mergedParams = Object.assign({}, params, { cat: "pickup" });
  const res = await apiClient.get("/api/wp-json/31corp/v1/posts", {
    params: mergedParams,
  });
  return res.data;
}

export async function fetchPostsNews(params: {
  offset?: number;
  length?: number;
  id?: number;
  lang?: string;
}): Promise<ApiPostsNews> {
  const mergedParams = Object.assign({}, params, { cat: "info" });
  const res = await apiClient.get("/api/wp-json/31corp/v1/posts", {
    params: mergedParams,
  });
  return res.data;
}

export async function fetchSpecialOffer(
  params: {
    offset?: number;
    length?: number;
    id?: number;
    lang?: string;
  } = { lang: "ja" }
): Promise<ApiPostsSpecialoffer> {
  const mergedParams = Object.assign({}, params, { cat: "offer" });
  const res = await apiClient.get("/api/wp-json/31corp/v1/posts", {
    params: mergedParams,
  });
  return res.data;
}

export async function fetchBanners(params: {
  cat: "is-login" | "login-page" | "popup-banner";
}): Promise<BannerData[]> {
  const res = await apiClient.get("/api/wp-json/31corp/v1/banners", { params });
  return res.data;
}

export async function fetchHotels(params: {
  lang?: string;
  hotel?: string;
  brand?: string;
  area?: string;
  q?: string;
  offset?: number;
  length?: number;
}): Promise<ApiHotels> {
  const res = await apiClient.get("/api/wp-json/31corp/v1/hotels", { params });
  return res.data;
}

export async function fetchSns(params: { lang?: string }): Promise<SNS[]> {
  const res = await apiClient.get("/api/wp-json/31corp/v1/sns", { params });
  return res.data;
}

export async function fetchLatest(): Promise<ApiLatest> {
  const res = await apiClient.get("/api/wp-json/31corp/v1/latest");
  return res.data;
}

export async function fetchReservable(params: {
  vacancy_date: string;
  vacancy_days: number;
  man_count: number;
  area: string;
  get_days?: number;
  lang: string;
}): Promise<ApiReservable> {
  const res = await apiClient.get("/api/wp-json/31corp/v1/reservable", {
    params,
  });
  return res.data;
}

export async function fetchPage(params: {
  name: string;
  lang: string;
}): Promise<ApiPage> {
  return await apiClient
    .get("/api/wp-json/31corp/v1/page", { params })
    .then((res) => {
      return res.data;
    })
    .catch(async (e: any) => {
      const status = e.response.status || 0;
      const message = e.response.data.message || e.message || "API Error";
      const code = e.response.data.code || e.code || "API Error";
      if (status === 500 && code === "Cannot access LINE REST Server") {
        throw new ApiError(500, "Line connect Error");
      }
      throw new ApiError(status, message);
    });
}

export async function fetchRecommend(params: {
  lang: string;
}): Promise<string[]> {
  const res = await apiClient.get("/api/wp-json/31corp/v1/recommend", {
    params,
  });
  return res.data;
}

/**
 * @param params　LINEアクセストークン
 * @returns LINEの友達関係を取得する
 */
export async function fetchLineFriendship(params: {
  lineAccessToken: string;
  lineIdToken: string;
}): Promise<{
  friendFlag: boolean;
  user_id?: string;
}> {
  return await apiClient
    .get("/api/wp-json/31corp/v1/line/friendship", {
      headers: {
        "X-Line-Access-Token": `${params.lineAccessToken}`,
        "X-Line-Id-Token": `${params.lineIdToken}`,
      },
    })
    .then((res) => {
      return res.data;
    })
    .catch(async (e: any) => {
      if (isTokenExpired(e)) {
        await store.dispatch("auth/lineRefresh");
        return await apiClient
          .get("/api/wp-json/31corp/v1/line/friendship", {
            headers: {
              "X-Line-Access-Token": `${params.lineAccessToken}`,
              "X-Line-Id-Token": `${params.lineIdToken}`,
            },
          })
          .then((res) => {
            return res.data;
          });
      } else {
        const status = e.response.status || 0;
        const message = e.response.data.message || e.message || "API Error";
        const code = e.response.data.code || e.code || "API Error";
        if (status === 500 && code === "Cannot access LINE REST Server") {
          throw new ApiError(500, "Line connect Error");
        }
        throw new ApiError(status, message);
      }
    });
}

/**
 * LINE認可コードでログイン
 * @param params LINE 認可コード
 * @returns LINEのアクセストークン、IDトークン
 */
export async function fetchLineLogin(params: {
  code: string;
  redirect_uri: string;
}): Promise<LineLoginResponse> {
  return await apiClient
    .post("/api/wp-json/31corp/v1/line/login", params)
    .then((res) => {
      return res.data;
    })
    .catch(async (e: any) => {
      if (isTokenExpired(e)) {
        await store.dispatch("auth/lineRefresh");
        return await apiClient
          .post("/api/wp-json/31corp/v1/line/login", params)
          .then((res) => {
            return res.data;
          });
      } else {
        const status = e.response.status || 0;
        const message = e.response.data.message || e.message || "API Error";
        const code = e.response.data.code || e.code || "API Error";
        if (status === 500 && code === "Cannot access LINE REST Server") {
          throw new ApiError(500, "Line connect Error");
        }
        throw new ApiError(status, message);
      }
    });
}

/**
 * LINE IDと所属先IDを連携・更新をする
 * @param params LINEアクセストークン、31corpトークン
 * @returns アップデートの成功可否
 */
export async function fetchLineRegister(accessToken: string): Promise<string> {
  return await apiClient
    .post(
      "/api/wp-json/31corp/v1/line/register",
      {},
      {
        headers: {
          "X-Line-Access-Token": `${accessToken}`,
        },
      }
    )
    .then((res) => {
      return res.data;
    })
    .catch(async (e: any) => {
      if (isTokenExpired(e)) {
        await store.dispatch("auth/lineRefresh");
        return await apiClient
          .post(
            "/api/wp-json/31corp/v1/line/register",
            {},
            {
              headers: {
                "X-Line-Access-Token": `${accessToken}`,
              },
            }
          )
          .then((res) => {
            return res.data;
          });
      } else {
        const status = e.response.status || 0;
        const message = e.response.data.message || e.message || "API Error";
        const code = e.response.data.code || e.code || "API Error";
        if (status === 500 && code === "Cannot access LINE REST Server") {
          throw new ApiError(500, "Line connect Error");
        }
        throw new ApiError(status, message);
      }
    });
}

/**
 * フロント側からlineアクセストークンを更新する
 * @returns 新しいLINEアクセストークン
 */
export async function fetchLineRefresh(): Promise<string> {
  return await apiClient
    .get("/api/wp-json/31corp/v1/line/refresh")
    .then((res) => {
      return res.data;
    })
    .catch((e: any) => {
      const status = e.response.status || 0;
      const message = e.response.data.message || e.message || "API Error";
      const code = e.response.data.code || e.code || "API Error";
      if (status === 500 && code === "Cannot access LINE REST Server") {
        throw new ApiError(500, "Line connect Error");
      }
      throw new ApiError(status, message);
    });
}

/**
 * LINE連携解除
 * @returns 新しいLINEアクセストークン
 */
export async function fetchLineDelete(): Promise<string> {
  return await apiClient
    .post("/api/wp-json/31corp/v1/line/delete")
    .then((res) => {
      return res.data;
    })
    .catch(async (e: any) => {
      if (isTokenExpired(e)) {
        await store.dispatch("auth/lineRefresh");
        return await apiClient
          .post("/api/wp-json/31corp/v1/line/delete")
          .then((res) => {
            return res.data;
          });
      } else {
        const status = e.response.status || 0;
        const message = e.response.data.message || e.message || "API Error";
        const code = e.response.data.code || e.code || "API Error";
        if (status === 500 && code === "Cannot access LINE REST Server") {
          throw new ApiError(500, "Line connect Error");
        }
        throw new ApiError(status, message);
      }
    });
}

export async function fetchLineMessage(params: {
  slug: string;
}): Promise<string> {
  return await apiClient
    .post("/api/wp-json/31corp/v1/line/message", params)
    .then((res) => {
      return res.data;
    })
    .catch(async (e: any) => {
      if (isTokenExpired(e)) {
        await store.dispatch("auth/lineRefresh");
        return await apiClient
          .post("/api/wp-json/31corp/v1/line/message", params)
          .then((res) => {
            return res.data;
          });
      } else {
        const status = e.response.status || 0;
        const message = e.response.data.message || e.message || "API Error";
        const code = e.response.data.code || e.code || "API Error";
        if (status === 500 && code === "Cannot access LINE REST Server") {
          throw new ApiError(500, "Line connect Error");
        }
        throw new ApiError(status, message);
      }
    });
}

/**
 * MGH RWC連携を行う
 * @param params Lineアクセストークン
 * @returns MGHRWC会員情報
 */
export async function fetchMghrwcLogin(params: {
  mghrwc_mail: string;
  mghrwc_pw: string;
}): Promise<{
  clubno: string;
}> {
  return await apiClient
    .post("/api/wp-json/jwt-auth/v1/mghrwc/login", params)
    .then((res) => {
      return res.data;
    })
    .catch((e: any) => {
      const status = e.response.status || 0;
      const message = e.response.data.message || e.message || "API Error";
      if (
        status === 500 &&
        message ===
          "MGH Rewards Club との連携に失敗しました。入力内容を再度ご確認ください"
      ) {
        throw new ApiError(500, "MGHRWC connect Error");
      }
      throw new ApiError(status, message);
    });
}

/**
 * MGH RWC SSOトークン取得
 * @param params Lineアクセストークン
 * @returns MGH RWC SSOトークン
 */
export async function fetchMghrwcToken(params: {
  action: string;
  htlcd?: string;
  regino?: string;
  lang: "ja-JP" | "en-US";
}): Promise<{
  token: string;
  id: string;
  regId: string;
}> {
  return await apiClient
    .post("/api/wp-json/31corp/v1/mghrwc/token", params)
    .then((res) => {
      return res.data;
    })
    .catch((e: any) => {
      const status = e.response.status || 0;
      const message = e.response.data.message || e.message || "API Error";
      if (
        status === 500 &&
        message ===
          "MGH Rewards Club との連携に失敗しました。入力内容を再度ご確認ください"
      ) {
        throw new ApiError(500, "MGHRWC connect Error");
      }
      throw new ApiError(status, message);
    });
}

/**
 * SIDを取得する
 * @param params token と clubno
 * @returns SID
 */
export async function fetchSid(params: {
  token: string;
  clubno: string;
}): Promise<string> {
  return await apiClient
    .post("/api/wp-json/31corp/v1/mghrwc/sid", {
      token: params.token,
      clubno: params.clubno,
    })
    .then((res) => {
      return res.data.sid;
    })
    .catch((e: any) => {
      const status = e.response.status || 0;
      const message = e.response.data.message || e.message || "API Error";
      throw new ApiError(status, message);
    });
}
